'use client';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import 'swiper/css/virtual';

import cn from 'classnames';
import { useCallback, useState } from 'react';
import { Autoplay, EffectFade, FreeMode, Pagination, Virtual } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperTypes } from 'swiper/types';

import { Arrow } from '@/components/Icons';
import { useAnalytics } from '@/hooks/useAnalytics';

import s from './CarouselBase.module.scss';
import { FullSlide, MainSlide } from './slides';

const Navigation = ({
  className = '',
  arrowClassName = '',
  customArrow,
  onPrevClick,
  onNextClick,
  analyticsName,
}: {
  className?: string;
  arrowClassName?: string;
  customArrow?: React.ReactNode;
  onPrevClick(): void;
  onNextClick(): void;
  analyticsName: string;
}) => {
  const arrow = customArrow ? (
    customArrow
  ) : (
    <Arrow className={cn(s.arrow, arrowClassName)} />
  );

  const { clickAnalytics } = useAnalytics();

  return (
    <div className={cn(s.buttons, className)}>
      <button
        className={s.button}
        onClick={() => {
          onPrevClick();
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'buttonclick',
            clickContent: 'arrow-icon-prev',
            uniqueId: `carousel-prev-arrow-${analyticsName}`,
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
          });
        }}
        data-qa="carousel-button-left"
      >
        {arrow}
      </button>
      <button
        data-qa="carousel-button-right"
        className={cn(s.button, s.buttonRight)}
        onClick={() => {
          onNextClick();
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'buttonclick',
            clickContent: 'arrow-icon-next',
            uniqueId: `carousel-next-arrow-${analyticsName}`,
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
          });
        }}
      >
        {arrow}
      </button>
    </div>
  );
};

interface CarouselBaseProps {
  slidesCount: number;
  slidesPerView?: number | 'auto';
  slidesSpacing?: number;
  freeMode?: boolean;
  interval?: number;
  autoPlay?: boolean;
  customArrow?: React.ReactNode;
  effect?: 'slide' | 'fade';
  /** Использовать если слайдов меньше чем slidesPerView * 2 */
  rewind?: boolean;
  /** Использовать если слайдов >= slidesPerView * 2 */
  loop?: boolean;
  /** Устанавливает брейкпоинт >= указанного */
  breakpoints?: {
    [key: number]: {
      slidesPerView?: number | 'auto';
      spaceBetween?: number;
    };
  };
  className?: string;
  navigationClassName?: string;
  carouselClassName?: string;
  slideClassName?: string;
  paginationBulletClassName?: string;
  arrowClassName?: string;
  speed?: number;
  withNavigation?: boolean;
  direction?: 'vertical' | 'horizontal';
  /** Необходимо передавать lenght массива. Выводиться будет не более 6 буллетов. */
  withPagination?: boolean;
  /** customArrow={<Arrow />} */
  children: React.ReactNode[];
  slidesStyles?: { width: string }[];
  withoutNavigationDirectionClassName?: boolean;
  onSlideChangeTransitionStart?: () => void;
  onSlideChangeTransitionEnd?: () => void;
  preventInteractionOnTransition?: boolean;
  analyticsName: string;
}

const CarouselBase = ({
  slidesCount,
  className = '',
  navigationClassName = '',
  withNavigation,
  withPagination,
  slideClassName = '',
  slidesPerView = 1,
  slidesSpacing,
  direction = 'horizontal',
  withoutNavigationDirectionClassName,
  slidesStyles,
  paginationBulletClassName = '',
  carouselClassName = '',
  arrowClassName = '',
  interval = 9000,
  breakpoints,
  loop,
  freeMode,
  rewind,
  customArrow,
  autoPlay,
  children,
  effect = 'slide',
  speed = 300,
  analyticsName,
}: CarouselBaseProps) => {
  const [swiperRef, setSwiperRef] = useState<SwiperTypes>();

  const onPrevClickHandler = useCallback(() => {
    swiperRef?.slidePrev();
  }, [swiperRef]);

  const onNextClickHandler = useCallback(() => {
    swiperRef?.slideNext();
  }, [swiperRef]);

  const isPagination =
    withPagination && slidesCount <= 6
      ? {
          clickable: true,
          renderBullet: (_: number, className: string) => {
            return `<span class="${cn(
              className,
              s.bullet,
              paginationBulletClassName,
            )}"></span>`;
          },
        }
      : false;

  const isAutoPlay = autoPlay
    ? {
        delay: interval,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }
    : false;

  const isFreeMode = freeMode
    ? {
        enabled: true,
        sticky: true,
      }
    : false;

  return (
    <div className={cn(s.root, className)}>
      <Swiper
        onSwiper={setSwiperRef}
        className={carouselClassName}
        modules={[Autoplay, Virtual, Pagination, EffectFade, FreeMode]}
        spaceBetween={slidesSpacing}
        slidesPerView={slidesPerView}
        breakpoints={breakpoints}
        loop={loop}
        rewind={rewind}
        pagination={isPagination}
        virtual={slidesCount > 15}
        autoplay={isAutoPlay}
        freeMode={isFreeMode}
        direction={direction}
        effect={effect}
        speed={speed}
      >
        {children.map((child, index) => {
          return (
            <SwiperSlide
              className={cn(s.slide, slideClassName)}
              key={index}
              style={slidesStyles ? slidesStyles[index] : undefined}
            >
              {child}
            </SwiperSlide>
          );
        })}
      </Swiper>

      {withNavigation && (
        <Navigation
          onPrevClick={onPrevClickHandler}
          onNextClick={onNextClickHandler}
          className={cn(navigationClassName, {
            [s.buttonsVertical]:
              !withoutNavigationDirectionClassName && direction === 'vertical',
          })}
          customArrow={customArrow}
          arrowClassName={arrowClassName}
          analyticsName={analyticsName}
        />
      )}
    </div>
  );
};

CarouselBase.Main = MainSlide;
CarouselBase.Full = FullSlide;

export { CarouselBase };
